import { useEffect, useState } from "react";
import { KeyRound, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ROLE_LABELS, useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", email: "", password: "", role: "secretaria" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editForm, setEditForm] = useState({ name: "", role: "", active: true });
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/users", form);
      toast.success("Usuário criado.");
      setCreateOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.put(`/users/${editTarget.id}`, editForm);
      toast.success("Usuário atualizado.");
      setEditTarget(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/users/${passwordTarget.id}/password`, { password: newPassword });
      toast.success("Senha redefinida.");
      setPasswordTarget(null);
      setNewPassword("");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Usuários</h1>
          <p className="mt-1 text-sm text-muted-foreground">Contas de acesso e perfis de permissão.</p>
        </div>
        <Button onClick={() => { setError(""); setCreateOpen(true); }} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="user-add-button">
          <Plus size={16} className="mr-2" /> Novo usuário
        </Button>
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">E-mail</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Perfil</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="transition-colors hover:bg-zinc-50" data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{ROLE_LABELS[u.role] || u.role}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={u.active ? "default" : "secondary"} className={u.active ? "bg-primary text-primary-foreground" : ""}>
                    {u.active ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Editar"
                    onClick={() => { setError(""); setEditTarget(u); setEditForm({ name: u.name, role: u.role, active: u.active }); }}
                    data-testid={`user-edit-${u.email}`}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Redefinir senha"
                    onClick={() => { setError(""); setPasswordTarget(u); setNewPassword(""); }}
                    data-testid={`user-password-${u.email}`}
                  >
                    <KeyRound size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="user-create-dialog">
          <DialogHeader>
            <DialogTitle>Novo usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="user-create-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Senha inicial</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="user-create-save-button">
              Criar usuário
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="user-edit-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="user-edit-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Perfil</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={editTarget?.id === me.id}>
                <SelectTrigger data-testid="user-edit-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={editForm.active}
                onCheckedChange={(v) => setEditForm({ ...editForm, active: v })}
                disabled={editTarget?.id === me.id}
                data-testid="user-edit-active-switch"
              />
              <Label>Usuário ativo</Label>
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="user-edit-save-button">
              Salvar
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!passwordTarget} onOpenChange={() => setPasswordTarget(null)}>
        <DialogContent data-testid="user-password-dialog">
          <DialogHeader>
            <DialogTitle>Redefinir senha — {passwordTarget?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={savePassword} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="user-password-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nova senha</Label>
              <Input type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="user-new-password-input" />
            </div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="user-password-save-button">
              Redefinir senha
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
